// A complete sample deck (one slide of every layout) and its context, for the dev route /d/__sample, the editor's
// empty states and tests. Copy is a real Store Owner pitch; numbers follow the public pricing widget's maths.
import { DeckContext, DeckDoc, DeckImage, PublicDeck } from './types';

const ASSETS = 'https://qosvfafzefkayfspufbt.supabase.co/storage/v1/object/public/marketing-assets/w1/';
const asset = (id: number, file: string, alt: string): DeckImage => ({ url: `${ASSETS}${file}`, alt, asset_id: id });
const shop = (file: string, alt: string): DeckImage => ({ url: `https://cdn.shopify.com/s/files/1/0108/8225/2863/files/${file}`, alt, asset_id: null });

export const SAMPLE_LOGO = 'https://cdn.mcauto-images-production.sendgrid.net/355fd3e801d414bb/8be46b79-bafb-4d30-a6c4-91323b029118/200x46.png';

const ROOMS = {
    living: asset(133, '1793822184343-7wnn3r-wallmates-room-living-floral-hero.jpg', 'Sunlit living room with a coral peony accent wall'),
    nursery: asset(134, '1793822188669-1qq2oq-wallmates-room-nursery-botanical-hero.jpg', 'Nursery in a lavender sweet-pea botanical'),
    bedroom: asset(135, '1793822193517-0hks4w-wallmates-room-bedroom-azure-hero.jpg', 'Bedroom with a Royal Azure arch geometric feature wall'),
    powder: asset(136, '1793822198953-ifd0d3-wallmates-room-powder-room-forest-hero.jpg', 'Powder room in a Forest Shade tropical botanical'),
    office: asset(137, '1793822204347-3xalut-wallmates-room-home-office-pattern-hero.jpg', 'Home office with a fern stripe wallpaper'),
    dining: asset(138, '1793822209976-axkf96-wallmates-room-dining-nook-coral-hero.jpg', 'Dining nook in coral peony and ranunculus'),
    after: asset(142, '1793822229637-5brlsq-wallmates-room-after-finished-wall.jpg', 'Living room finished in a coral and lavender floral')
};
const FOUNDER_BOXING = asset(145, '1793823386926-wowmbw-sunburst-04-ship-ready.jpg', 'A wallpaper brand founder boxing a coral floral roll');
const FOUNDER = asset(146, '1793823392108-zy2l8i-sunburst-08-one-person-brand.jpg', 'A one-person wallpaper brand owner with coffee');
const TEAM_MEMBER = asset(100, '1793821130669-rwuvo4-wallmates-photo-team-member-warehouse-kraft-paper.jpg', 'A Wallmates team member wrapping an order');
const FLATLAY = asset(139, '1793822214004-5llnz4-wallmates-room-roll-flatlay-product.jpg', 'A coral peony wallpaper roll with colourway swatches');

// #region SAMPLE_DOC
export const SAMPLE_DOC: DeckDoc = {
    version: 1,
    slides: [
        { id: 'cover', layout: 'cover', fields: {
            eyebrow: 'Store Owner partnership',
            title: 'Wallpaper, sold under {{lead.company|your brand}}',
            subtitle: 'You design and sell on your own Shopify store. We print, cut and ship every order, so you never hold a roll of stock.',
            prepared_for: null, image: ROOMS.living, show_date: true
        } },
        { id: 'why', layout: 'statement', fields: {
            eyebrow: 'Why wallpaper, why now',
            title: 'Your customers already love your patterns. **Let them live with them.**',
            body: 'Wallpaper is one of the few home categories where people buy the brand, not the commodity. A single feature wall is a $400 to $900 order, and it sells best from a shop people already follow.\n\n- No minimums, no inventory\n- Your brand on every box'
        } },
        { id: 'what-we-do', layout: 'bullets', fields: {
            eyebrow: 'What Wallmates handles',
            title: 'Everything after the sale is on us',
            items: [
                { title: 'Printed to order', body: 'Every roll is printed when your customer checks out, sized to their wall in 19-inch panels.' },
                { title: 'Cut, rolled and boxed', body: 'Panelized, trimmed and packed in our studio with install instructions in the box.' },
                { title: 'Shipped in your name', body: 'Orders go out with tracking straight to your customer. You never touch a box.' },
                { title: 'Synced with Shopify', body: 'Orders flow in automatically and tracking flows back, so your store stays the source of truth.' }
            ],
            image: FOUNDER_BOXING
        } },
        { id: 'how', layout: 'steps', fields: {
            eyebrow: 'How it works',
            title: 'Live on your store in about a week',
            items: [
                { title: 'Connect Shopify', body: 'Install the Wallmates app and link your store in a couple of clicks.' },
                { title: 'Upload your designs', body: 'Send your repeat files. We proof every pattern at full scale before it goes live.' },
                { title: 'Set your prices', body: 'You choose the retail price per square foot. Your margin is yours to keep.' },
                { title: 'We print and ship', body: 'Every order is printed, packed and shipped within days, with tracking back to you.' }
            ]
        } },
        { id: 'materials', layout: 'materials', fields: {
            eyebrow: 'Materials',
            title: 'Three papers, one quality bar',
            items: [
                { title: 'Traditional', body: 'A matte, paste-the-wall paper with a soft hand. The designer favourite for long-lasting feature walls.', image: FLATLAY },
                { title: 'Peel & Stick', body: 'Self-adhesive and repositionable. Renter-friendly and the easiest first wallpaper your customers will buy.', image: shop('Peel-_-Stick-Material.png?v=1770411128', 'Peel & Stick material swatch') },
                { title: 'Luxe Peel & Stick', body: 'A heavier, textured peel and stick with a premium finish that reads like a traditional paper on the wall.', image: shop('Luxe-Peel-_-Stick-Material.png?v=1770411129', 'Luxe Peel & Stick material swatch') }
            ]
        } },
        { id: 'designs', layout: 'designs', fields: { eyebrow: 'Your designs on the wall', title: 'Your collection, on real walls', source: 'lead_designs', max: 6 } },
        { id: 'room', layout: 'image', fields: { image: ROOMS.bedroom, title: 'Made for walls people photograph', caption: 'Royal Azure arch geometric, Traditional paper, printed to order.', dark: true } },
        { id: 'quote', layout: 'quote', fields: {
            quote: 'We launched wallpaper on a Tuesday and had our first order by Friday. I haven’t packed a single box. It just **shows up at our customers’ doors**.',
            by: 'Amara Lewis', role: 'Founder, Sunburst Studio', image: FOUNDER
        } },
        { id: 'stats', layout: 'stats', fields: {
            eyebrow: 'By the numbers', title: 'A print partner that scales with you',
            items: [{ value: '19 in', label: 'panels, printed to the exact size of each wall' }, { value: '3–5 days', label: 'typical time from order to shipped' }, { value: '$0', label: 'upfront: no minimums, no inventory' }]
        } },
        { id: 'pricing', layout: 'pricing', fields: {
            eyebrow: 'What you could earn', title: 'Your margin on every wall', tier: 'store_owner',
            materials: ['traditional', 'peel', 'peel_luxe'], retail_sqft: null, wall_width_ft: 12, wall_height_ft: 9, orders_per_month: 10, royalty_percent: null,
            note: 'You set your own retail price. These use what brands typically charge today.'
        } },
        { id: 'terms', layout: 'terms', fields: {
            eyebrow: 'The offer', title: 'Simple terms, no surprises',
            items: [
                { label: 'Setup fee', value: 'None. Free to connect and list your designs.' },
                { label: 'Minimums', value: 'No minimum orders and no inventory to buy.' },
                { label: 'Pricing tier', value: 'Store Owner rates on every material.' },
                { label: 'Samples', value: 'A free sample kit of all three materials for {{lead.company|your team}}.' },
                { label: 'Payment', value: 'Charged per order, only when your customer pays you.' },
                { label: 'Contract', value: 'Month to month. Leave any time.' }
            ],
            valid_until: '2026-12-04', signoff: 'We’d love to print for {{lead.company|you}}. {{rep.first_name}}'
        } },
        { id: 'team', layout: 'team', fields: {
            title: 'The people behind your orders', show_rep: true,
            items: [{ title: 'Jordan Reyes', body: 'Production lead', image: TEAM_MEMBER }, { title: 'Priya Natarajan', body: 'Design proofing', image: null }]
        } },
        { id: 'cta', layout: 'cta', fields: {
            title: 'Let’s put {{lead.company|your designs}} on walls',
            body: 'Book 20 minutes and we’ll set up your store together, with your first designs proofed on the call.',
            button_label: 'Book a call', button_url: ''
        } },
        { id: 'compare', layout: 'table', fields: {
            eyebrow: 'Compared', title: 'Wallmates vs. doing it yourself',
            rows: [
                ['', 'Wallmates', 'Print in-house', 'Wholesale stock'],
                ['Upfront cost', '$0', '$40k+ printer', '$5k+ inventory'],
                ['Minimum order', 'None', 'None', '50+ rolls'],
                ['Who ships', 'We do', 'You do', 'You do'],
                ['Custom sizes', 'Every order', 'If you cut them', 'No'],
                ['Time to launch', 'About a week', 'Months', 'Weeks']
            ]
        } }
    ]
};

// #region SAMPLE_CONTEXT
export const SAMPLE_CONTEXT: DeckContext = {
    theme: {
        name: 'Wallmates',
        accent: '#006c56', accent_dark: '#2fbf98', ink: '#16201c', body: '#33403b', muted: '#66736e', page: '#f4f5f2', card: '#ffffff', line: '#e3e8e5',
        eyebrow: '#006c56', secondary: '#f3b9a6',
        font: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        heading_font: "'Clash Grotesk', 'Inter', system-ui, sans-serif", heading_weight: 600, heading_tracking: -0.015,
        font_css_url: 'https://api.fontshare.com/v2/css?f[]=clash-grotesk@500,600&display=swap',
        logo_url: SAMPLE_LOGO,
        radius: 20,
        frame: { from: '#006c56', to: '#2fbf98' }
    },
    values: {
        'lead.first_name': 'Ava', 'lead.last_name': 'Morales', 'lead.full_name': 'Ava Morales', 'lead.company': 'Fern & Field', 'lead.website': 'https://fernandfield.co', 'lead.email': 'ava@fernandfield.co',
        'rep.name': 'Sam Carter', 'rep.first_name': 'Sam', 'rep.title': 'Partnerships, Wallmates', 'rep.email': 'sam@wallmates.com', 'rep.phone': '(801) 555-0142',
        'rep.booking_link': 'https://dashboard.wallmates.com/book/sam',
        'company.name': 'Wallmates', 'company.website': 'https://www.wallmates.com', today: 'November 4, 2026', valid_until: 'December 4, 2026'
    },
    computed: {
        designs: { designs: { items: [ROOMS.after, ROOMS.nursery, ROOMS.powder, ROOMS.office, ROOMS.dining, ROOMS.living].map((image, i) => ({
            title: ['Peony Parade', 'Sweet Pea', 'Forest Shade', 'Fern Stripe', 'Ranunculus', 'Coral Bloom'][i], image
        })) } },
        pricing: { pricing: {
            tier: 'store_owner', currency: 'USD', wall: { width_ft: 12, height_ft: 9, panels: 8, sqft: 126.67 }, orders_per_month: 10, royalty_percent: null,
            fees_note: 'Fulfillment fee is 2.75% of the reference rate, plus $3.00 order processing per order. Shipping is charged to your customer.',
            rows: [
                { material: 'traditional', label: 'Traditional', cost_sqft: 1.34, retail_sqft: 5.37, per_order: 494.56, monthly: 4945.6, margin_pct: 72.7,
                    breakdown: [{ label: 'Your customer pays', amount: 680.22 }, { label: 'Material', amount: -169.74 }, { label: 'Fulfillment fee', amount: -12.92 }, { label: 'Order processing', amount: -3 }] },
                { material: 'peel', label: 'Peel & Stick', cost_sqft: 1.99, retail_sqft: 6.5, per_order: 554.25, monthly: 5542.5, margin_pct: 67.3,
                    breakdown: [{ label: 'Your customer pays', amount: 823.36 }, { label: 'Material', amount: -252.07 }, { label: 'Fulfillment fee', amount: -14.04 }, { label: 'Order processing', amount: -3 }] },
                { material: 'peel_luxe', label: 'Luxe Peel & Stick', cost_sqft: 2.49, retail_sqft: 7.95, per_order: 671.59, monthly: 6715.9, margin_pct: 66.7,
                    breakdown: [{ label: 'Your customer pays', amount: 1007.03 }, { label: 'Material', amount: -315.41 }, { label: 'Fulfillment fee', amount: -17.03 }, { label: 'Order processing', amount: -3 }] }
            ]
        } },
        team: { team: { rep: { name: 'Sam Carter', title: 'Partnerships, Wallmates', photo_url: null, phone: '(801) 555-0142', email: 'sam@wallmates.com', booking_link: 'https://dashboard.wallmates.com/book/sam' } } }
    }
};

// #region SAMPLE_PUBLIC_DECK
/** What GET /public/decks/:token would return for the sample. */
export const SAMPLE_PUBLIC_DECK: PublicDeck = {
    title: 'Wallmates × Fern & Field',
    doc: SAMPLE_DOC,
    context: SAMPLE_CONTEXT,
    prepared_for: 'Fern & Field',
    rep: { name: 'Sam Carter', title: 'Partnerships, Wallmates', photo_url: null, booking_link: 'https://dashboard.wallmates.com/book/sam' },
    expires_at: '2026-12-04T00:00:00Z'
};

// #region SAMPLE_LONG_DOC
const FILLER = 'Every order is printed to the exact wall size, trimmed, rolled and boxed by our team, then shipped with tracking straight to your customer while you keep designing new collections ';
const pad = (n: number): string => { let t = ''; while (t.length < n) t += FILLER; return t.slice(0, n).trim(); };
const TEXT_MAX: Record<string, number> = { eyebrow: 40, title: 90, subtitle: 160, prepared_for: 80, body: 600, quote: 320, by: 60, role: 80, caption: 200, note: 200, signoff: 120, button_label: 30 };
const ITEM_LIMITS: Partial<Record<string, { count: number; title?: number; body?: number; value?: number; label?: number }>> = {
    bullets: { count: 6, title: 60, body: 240 }, steps: { count: 5, title: 50, body: 200 }, materials: { count: 4, title: 40, body: 220 },
    stats: { count: 4, value: 16, label: 60 }, terms: { count: 8, label: 40, value: 120 }, team: { count: 3, title: 60, body: 80 }
};

/** Every text field at its contract limit and every list at its longest: for checking that nothing overflows. */
export function longDoc(doc: DeckDoc = SAMPLE_DOC): DeckDoc {
    return { version: 1, slides: doc.slides.map((s) => {
        const fields = { ...s.fields };
        for (const [k, v] of Object.entries(fields)) {
            if (typeof v !== 'string' || !(k in TEXT_MAX)) continue;
            const max = k === 'title' && s.layout === 'statement' ? 120 : k === 'body' && s.layout === 'cta' ? 300 : TEXT_MAX[k];
            fields[k] = pad(max);
        }
        const lim = ITEM_LIMITS[s.layout];
        if (lim && Array.isArray(fields.items)) {
            const base = fields.items as { image?: DeckImage | null }[];
            fields.items = Array.from({ length: lim.count }, (_, i) => ({
                ...(lim.title ? { title: pad(lim.title) } : {}), ...(lim.body ? { body: pad(lim.body) } : {}),
                ...(lim.value ? { value: '$1,234,567.89' } : {}), ...(lim.label ? { label: pad(lim.label) } : {}),
                image: base[i % base.length]?.image ?? null
            }));
        }
        if (s.layout === 'table') fields.rows = Array.from({ length: 8 }, () => Array.from({ length: 5 }, () => pad(60)));
        if (s.layout === 'designs') fields.max = 8;
        return { ...s, fields };
    }) };
}
